import { Chart, ChartConfiguration, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Plugin } from 'chart.js';
import { Statistics } from '../../../../../model/activity.model';
import { makeSummaryChartData, makeSummaryChartOptions } from '../ActivityBars/helper';
import { getPieChartData, makePieChartOptions } from '../ActivityPie/helper';

// Register required Chart.js components
Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Plugin to draw white background (needed for JPEG export)
const whiteBackgroundPlugin: Plugin = {
  id: 'whiteBackground',
  beforeDraw: (chart) => {
    const ctx = chart.canvas.getContext('2d');
    if (ctx) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    }
  }
};

export interface ChartImages {
  barChart: string;
  pieChart: string;
}

export const renderChartsForPdf = async (
  statistics: Statistics,
  grouped: boolean,
  _isDark: boolean
): Promise<ChartImages> => {
  // Create temporary canvas elements - smaller sizes for reasonable PDF file size
  const barCanvas = document.createElement('canvas');
  const pieCanvas = document.createElement('canvas');

  // Reduced sizes - still sufficient for A4 print quality
  barCanvas.width = 500;
  barCanvas.height = 200;
  pieCanvas.width = 250;
  pieCanvas.height = 250;

  // Create bar chart with white background plugin
  const barData = makeSummaryChartData(statistics, grouped);
  const barOptions = makeSummaryChartOptions(false, false);

  const barChart = new Chart(barCanvas, {
    type: 'bar',
    data: barData,
    options: {
      ...barOptions,
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
    },
    plugins: [whiteBackgroundPlugin]
  } as ChartConfiguration<'bar'>);

  // Create pie chart with white background plugin
  const statsItems = grouped ? statistics.total.groupedStatistics : statistics.total.statistics;
  const pieData = getPieChartData(statsItems, statistics.types, false);
  const pieOptions = makePieChartOptions(statsItems, false);

  const pieChart = new Chart(pieCanvas, {
    type: 'doughnut',
    data: pieData,
    options: {
      ...pieOptions,
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
    },
    plugins: [whiteBackgroundPlugin]
  } as ChartConfiguration<'doughnut'>);

  // Wait for charts to render
  await new Promise(resolve => setTimeout(resolve, 50));

  // Capture images - use JPEG with 0.8 quality for smaller file size
  const barImage = barChart.toBase64Image('image/jpeg', 0.8);
  const pieImage = pieChart.toBase64Image('image/jpeg', 0.8);

  // Cleanup
  barChart.destroy();
  pieChart.destroy();

  return { barChart: barImage, pieChart: pieImage };
};
